import { useCallback, useEffect, useState } from "react";

import config from "console_config";
import { selectCurrentUserIsInternal } from "Reducers/app/selectors";
import { useAppSelector } from "Store/hooks";

import { loadManifest } from "./loadManifest";
import { PluginManifest } from "./plugin.types";

type PluginState = Record<string, PluginManifest>;

export function usePluginApps() {
  const [pluginApps, setApps] = useState<PluginState>({});
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const currentUserIsInternal = useAppSelector(selectCurrentUserIsInternal);

  const persistApps = useCallback(
    (newApps: PluginState | ((prev: PluginState) => PluginState)) => {
      setApps(apps => {
        if (typeof newApps === "function") newApps = newApps(apps);
        localStorage.setItem("plugin-apps", JSON.stringify(newApps));
        return newApps;
      });
    },
    []
  );

  useEffect(() => {
    (async () => {
      const allApps: PluginState = {};

      if (!currentUserIsInternal) {
        setIsLoading(false);
        setApps(allApps);
        return;
      }

      const localStorageApps = localStorage.getItem("plugin-apps");
      if (localStorageApps) {
        const parsedApps: PluginState = JSON.parse(localStorageApps);
        for (const [appId, manifest] of Object.entries(parsedApps)) {
          allApps[appId] = manifest;
        }
      }

      if (config.CUSTOM_DEFAULT_PLUGINS) {
        const installedPlugins = Object.values(allApps).map(app => app._self);
        for (const manifestUrl of config.CUSTOM_DEFAULT_PLUGINS) {
          if (installedPlugins.includes(manifestUrl)) continue;
          try {
            const manifest = await loadManifest(manifestUrl);
            allApps[manifest.id] = manifest;
          } catch (err) {
            setError(`Failed to load the manifest. ${err}`);
          }
        }
      }

      setIsLoading(false);
      persistApps(allApps);
    })();
  }, [currentUserIsInternal, persistApps]);

  return [pluginApps, persistApps, error, isLoading] as const;
}
